import { useState, useEffect, useCallback } from 'react'
import { Lock, Unlock, Plus, Loader2, KeyRound, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useVault, type ClaveCliente } from '@/hooks/useVault'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { PinInput } from './PinInput'
import { ClaveCard } from './ClaveCard'
import { toast } from 'sonner'
import { ORGANISMOS, sanitizeVaultError } from '@/lib/cajaFuerte'

interface CajaFuertePanelProps {
  clienteId: string
  cuit?: string | null
}

export function CajaFuertePanel({ clienteId, cuit }: CajaFuertePanelProps) {
  const vault = useVault()
  const { organizacionId, membresia } = useOrganizacion()
  const { puede } = usePermissions()
  // Mismo criterio que vault-claves: Solo lectura no ve claves; cambiarlas pide editarCliente.
  // El asistente solo abre fichas de su cartera, así que ver acá ya implica que es responsable.
  const puedeVer = membresia?.rol !== 'solo_lectura'
  const puedeEditar = puedeVer && puede.editarCliente === true
  const [claves, setClaves] = useState<ClaveCliente[]>([])
  const [cargando, setCargando] = useState(false)
  const [pinError, setPinError] = useState(false)
  const [verificandoPin, setVerificandoPin] = useState(false)

  // Form dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editando, setEditando] = useState<ClaveCliente | null>(null)
  const [formData, setFormData] = useState({
    organismo: '',
    organismoCustom: '',
    usuario: '',
    clave: '',
    notas: '',
  })
  const [guardando, setGuardando] = useState(false)

  // Delete confirmation
  const [eliminarId, setEliminarId] = useState<string | null>(null)

  // PIN setup state
  const [pinSetup, setPinSetup] = useState('')
  const [setupStep, setSetupStep] = useState<'enter' | 'confirm'>('enter')

  // Check PIN status on mount
  useEffect(() => {
    vault.checkPinStatus()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault.checkPinStatus])

  // Load claves when unlocked
  const cargarClaves = useCallback(async () => {
    setCargando(true)
    try {
      const data = await vault.obtenerClaves(clienteId)
      setClaves(data ?? [])
    } catch (err) {
      console.error('Error loading claves:', err)
      toast.error(sanitizeVaultError(err, 'Error al cargar claves'))
    } finally {
      setCargando(false)
    }
  }, [clienteId, vault.obtenerClaves])

  useEffect(() => {
    if (vault.desbloqueado && puedeVer) {
      cargarClaves()
    } else {
      setClaves([])
    }
  }, [vault.desbloqueado, puedeVer, cargarClaves])

  // PIN verification handler
  const handlePinSubmit = async (pin: string) => {
    setPinError(false)
    setVerificandoPin(true)
    const valid = await vault.verificarPin(pin)
    setVerificandoPin(false)
    if (!valid) {
      setPinError(true)
    }
  }

  // PIN setup handler
  const handlePinSetup = (pin: string) => {
    if (setupStep === 'enter') {
      setPinSetup(pin)
      setSetupStep('confirm')
    } else {
      if (pin === pinSetup) {
        vault.configurarPin(pin)
        setSetupStep('enter')
        setPinSetup('')
      } else {
        setPinError(true)
        toast.error('Los PINs no coinciden')
        setSetupStep('enter')
        setPinSetup('')
        setTimeout(() => setPinError(false), 1500)
      }
    }
  }

  // Form handlers
  const openCreateDialog = () => {
    setEditando(null)
    setFormData({ organismo: '', organismoCustom: '', usuario: cuit || '', clave: '', notas: '' })
    setDialogOpen(true)
  }

  const openEditDialog = (clave: ClaveCliente) => {
    setEditando(clave)
    const esCustom = !ORGANISMOS.includes(clave.organismo)
    setFormData({
      organismo: esCustom ? 'Otro' : clave.organismo,
      organismoCustom: esCustom ? clave.organismo : '',
      usuario: clave.usuario || '',
      clave: clave.clave ?? '',
      notas: clave.notas || '',
    })
    setDialogOpen(true)
  }

  const handleGuardar = async () => {
    const organismo = formData.organismo === 'Otro'
      ? formData.organismoCustom
      : formData.organismo

    if (!organismo || !formData.clave) {
      toast.error('Organismo y clave son requeridos')
      return
    }

    if (!vault.desbloqueado) {
      toast.error('La sesión del PIN expiró. Ingresá el PIN nuevamente.')
      return
    }

    if (!organizacionId) {
      toast.error('Error de organización. Recargá la página.')
      return
    }

    setGuardando(true)
    try {
      if (editando) {
        await vault.actualizarClave(editando.id, {
          organismo,
          usuario: formData.usuario || undefined,
          clave: formData.clave,
          notas: formData.notas || undefined,
        })
      } else {
        await vault.crearClave(clienteId, {
          organismo,
          usuario: formData.usuario || undefined,
          clave: formData.clave,
          notas: formData.notas || undefined,
          organizacionId: organizacionId ?? undefined,
        })
      }
      setDialogOpen(false)
      cargarClaves()
    } catch (err) {
      console.error('Error guardando clave:', err)
      toast.error(sanitizeVaultError(err, 'Error al guardar clave'))
    } finally {
      setGuardando(false)
    }
  }

  const handleEliminar = async () => {
    if (!eliminarId) return

    if (!vault.desbloqueado) {
      toast.error('La sesión del PIN expiró. Ingresá el PIN nuevamente.')
      return
    }

    try {
      await vault.eliminarClave(eliminarId)
      setEliminarId(null)
      cargarClaves()
    } catch (err) {
      console.error('Error eliminando clave:', err)
      toast.error(sanitizeVaultError(err, 'Error al eliminar clave'))
    }
  }

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  // Loading state
  if (!puedeVer) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Lock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Tu rol no tiene acceso a las claves de los clientes.</p>
      </div>
    )
  }

  if (vault.pinLoading || vault.hasPin === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Setup PIN (first time)
  if (!vault.hasPin) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
        <div className="rounded-full bg-primary/10 p-4">
          <ShieldCheck className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Configurá tu PIN de seguridad</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {setupStep === 'enter'
              ? 'Elegí un PIN de 4 dígitos para proteger las claves de tus clientes'
              : 'Confirmá tu PIN ingresándolo nuevamente'}
          </p>
        </div>
        <PinInput
          onComplete={handlePinSetup}
          error={pinError}
        />
      </div>
    )
  }

  // Locked state
  if (!vault.desbloqueado) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
        <div className="rounded-full bg-muted p-4">
          {verificandoPin ? (
            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
          ) : (
            <Lock className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div>
          <h3 className="font-semibold text-lg">
            {verificandoPin ? 'Verificando PIN...' : 'Claves protegidas'}
          </h3>
          {!verificandoPin && (
            <p className="text-sm text-muted-foreground mt-1">
              Ingresá tu PIN para acceder
            </p>
          )}
        </div>
        {!verificandoPin && (
          <PinInput
            onComplete={handlePinSubmit}
            error={pinError}
          />
        )}
        {pinError && (
          <p className="text-sm text-destructive">PIN incorrecto</p>
        )}
      </div>
    )
  }

  // Unlocked state
  return (
    <div className="space-y-4">
      {/* Header with timer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Unlock className="h-4 w-4" />
          <span>Se bloquea en {formatTimer(vault.tiempoRestante)}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={vault.bloquear}>
          <Lock className="h-4 w-4 mr-1" />
          Bloquear
        </Button>
      </div>

      {/* Claves list */}
      {cargando ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : claves.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <KeyRound className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No hay claves guardadas para este cliente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {claves.map((clave) => (
            <ClaveCard
              key={clave.id}
              clave={clave}
              onEditar={openEditDialog}
              onEliminar={setEliminarId}
              editable={puedeEditar}
            />
          ))}
        </div>
      )}

      {/* Add button */}
      {puedeEditar && (
        <Button variant="outline" className="w-full" onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Agregar clave
        </Button>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editando ? 'Editar clave' : 'Agregar clave'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Organismo</Label>
              <Select
                value={formData.organismo}
                onValueChange={(v) => setFormData({ ...formData, organismo: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar organismo" />
                </SelectTrigger>
                <SelectContent>
                  {ORGANISMOS.map((org) => (
                    <SelectItem key={org} value={org}>
                      {org}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.organismo === 'Otro' && (
                <Input
                  placeholder="Nombre del organismo"
                  value={formData.organismoCustom}
                  onChange={(e) => setFormData({ ...formData, organismoCustom: e.target.value })}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Usuario / CUIT</Label>
              <Input
                placeholder="ej: 20-12345678-6"
                value={formData.usuario}
                onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Clave *</Label>
              <Input
                type="password"
                placeholder="Contraseña del organismo"
                value={formData.clave}
                onChange={(e) => setFormData({ ...formData, clave: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                placeholder="Notas adicionales (opcional)"
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGuardar} disabled={guardando}>
              {guardando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {editando ? 'Guardar' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!eliminarId} onOpenChange={(open) => !open && setEliminarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar clave?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
